/**
 * `DimeTerminalProvider` — the single host-level wrapper that all
 * `<DimeCloudApp>` mount points share. It owns:
 *
 *  1. **`ClerkProvider`** — a single Clerk instance for the app, resolving the
 *     publishable key from the named `environment` (or an explicit override).
 *     Hoisting this here prevents the "multiple ClerkProvider" error when two
 *     `<DimeCloudApp>` surfaces transition (e.g. drawer → fullscreen route).
 *
 *  2. **Environment context** — distributes `environment` and optional `apiUrl`
 *     so every `<DimeCloudApp>` below reads the same backend coordinates. This
 *     prevents the footgun where the Clerk key is resolved from one environment
 *     but the API URL from another (the API would reject the token).
 *
 * Hosts mount ONE provider above all terminal surfaces:
 *
 * ```tsx
 * <DimeTerminalProvider environment="testnet">
 *   <App />
 * </DimeTerminalProvider>
 * ```
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { ClerkProvider, useAuth } from '@clerk/react';

import {
  resolveClerkPublishableKey,
  type DimeEnvironment,
} from './environments.js';
import {
  createDimeSessionCache,
  type DimeSessionCache,
} from './sessionCache.js';

// ── internal context ────────────────────────────────────────────────────────

interface DimeTerminalContextValue {
  readonly environment?: DimeEnvironment;
  readonly apiUrl?: string;
  readonly isSatellite?: boolean;
  /**
   * Provider-lifetime auth/connection cache (see `sessionCache.ts`): mounts
   * below the provider come and go with host-chrome interactions, but the
   * minted identity and resolved grant survive here, so a remount reuses
   * them instead of re-minting (rate-limited on native-auth hosts).
   */
  readonly sessionCache: DimeSessionCache;
}

const DimeTerminalCtx = createContext<DimeTerminalContextValue | null>(null);

/**
 * Read the provider context. Throws a clear error when called outside
 * `<DimeTerminalProvider>` — fail-fast so the host knows immediately.
 * `caller` names the offender in the error.
 *
 * @internal — consumed by `DimeCloudApp` / `useDimeLinkAccount`; not part of
 * the public API.
 */
export function useDimeTerminalContext(
  caller = '<DimeCloudApp>',
): DimeTerminalContextValue {
  const ctx = useContext(DimeTerminalCtx);
  if (ctx == null) {
    throw new Error(
      `${caller} must be used inside a <DimeTerminalProvider>. ` +
        'Wrap your component tree in a single <DimeTerminalProvider> above ' +
        'all terminal surfaces.',
    );
  }
  return ctx;
}

/**
 * Non-throwing variant for components that work with or without the provider
 * (`DimeCloudTerminal` is mountable standalone — no provider means no shared
 * cache, and callers fall back to per-mount behavior).
 *
 * @internal
 */
export function useOptionalDimeTerminalContext(): DimeTerminalContextValue | null {
  return useContext(DimeTerminalCtx);
}

// ── public provider ─────────────────────────────────────────────────────────

export interface DimeTerminalProviderProps {
  /** Named backend to target. Default `'production'`. */
  readonly environment?: DimeEnvironment;
  /**
   * Explicit web-api base URL — wins over `environment` for the API client.
   * Note: does NOT influence the Clerk publishable key (a custom backend's
   * trusted issuer is unknowable here — pass `clerk.publishableKey` alongside
   * a custom `apiUrl`).
   */
  readonly apiUrl?: string;
  readonly clerk?: {
    /**
     * Clerk publishable key (`pk_test_…` / `pk_live_…`). Defaults to the
     * bundled key for `environment` (`DIME_CLERK_PUBLISHABLE_KEYS`). Pass
     * explicitly only when targeting a custom backend (`apiUrl`), whose
     * issuer the bundled keys won't match.
     */
    readonly publishableKey?: string;
    /**
     * Enable Clerk satellite mode. When `true`, this app delegates to a
     * primary Clerk instance on `domain` — required for embedded hosts
     * (e.g. Paradex) that use a different Clerk domain than the primary.
     */
    readonly isSatellite?: boolean;
    /**
     * The primary Clerk domain (e.g. `'paradex.trade'`).
     * Required when `isSatellite` is `true`.
     */
    readonly domain?: string;
    /**
     * Primary domain sign-in URL. Required when `isSatellite` is `true`.
     * e.g. `'https://app.dimeterminal.ai'`
     */
    readonly signInUrl?: string;
    /**
     * Primary domain sign-up URL. Required when `isSatellite` is `true`.
     * e.g. `'https://app.dimeterminal.ai'`
     */
    readonly signUpUrl?: string;
    /**
     * Origins the primary domain is allowed to redirect back to after
     * sign-in (satellite domains). Only meaningful on the primary app.
     */
    readonly allowedRedirectOrigins?: string[];
  };
  readonly children: ReactNode;
}

const SATELLITE_REDIRECT_KEY = '__dime_satellite_redirect';

/**
 * On the primary app, when a satellite's `buildSignInUrl()` lands here, the
 * URL contains `redirect_url=<satellite origin>` (in the hash fragment). After
 * sign-in completes Clerk doesn't navigate back automatically in a client-only
 * SPA (that's middleware's job in Next.js). This component handles it:
 *
 *  1. On mount, reads `redirect_url` from the hash and stashes it in
 *     sessionStorage — the hash is lost during OAuth redirects (Google etc.)
 *     because fragments are never sent to the server.
 *  2. When `isSignedIn` becomes true, reads the stashed URL, validates the
 *     origin against `allowedRedirectOrigins`, and navigates back.
 *
 * Only fires when `allowedRedirectOrigins` is set — satellites never set it.
 */
function SatelliteRedirectBack({
  allowedRedirectOrigins,
}: {
  allowedRedirectOrigins?: string[];
}): null {
  const { isSignedIn } = useAuth();

  // Step 1: stash redirect_url from the hash before OAuth can wipe it.
  useEffect(() => {
    if (allowedRedirectOrigins == null || allowedRedirectOrigins.length === 0)
      return;
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return;
    const params = new URLSearchParams(hash.slice(qIdx));
    const redirectUrl = params.get('redirect_url');
    if (redirectUrl == null) return;
    try {
      const target = new URL(redirectUrl);
      if (!allowedRedirectOrigins.includes(target.origin)) return;
    } catch {
      return;
    }
    sessionStorage.setItem(SATELLITE_REDIRECT_KEY, redirectUrl);
  }, [allowedRedirectOrigins]);

  // Step 2: after sign-in, redirect back to the satellite.
  useEffect(() => {
    if (!isSignedIn) return;
    const redirectUrl = sessionStorage.getItem(SATELLITE_REDIRECT_KEY);
    if (redirectUrl == null) return;
    sessionStorage.removeItem(SATELLITE_REDIRECT_KEY);
    window.location.href = redirectUrl;
  }, [isSignedIn]);

  return null;
}

/**
 * Single host-level Clerk + environment wrapper. Mount once, above all
 * `<DimeCloudApp>` surfaces.
 */
export function DimeTerminalProvider({
  environment,
  apiUrl,
  clerk,
  children,
}: DimeTerminalProviderProps): JSX.Element {
  const pk = resolveClerkPublishableKey({
    environment,
    publishableKey: clerk?.publishableKey,
  });
  const isSatellite = clerk?.isSatellite;
  // One cache per provider mount — exactly the lifetime the cached artifacts
  // should have (memory-only; dies with the app, survives surface remounts).
  const sessionCacheRef = useRef<DimeSessionCache | null>(null);
  sessionCacheRef.current ??= createDimeSessionCache();
  const sessionCache = sessionCacheRef.current;
  const ctx = useMemo<DimeTerminalContextValue>(
    () => ({ environment, apiUrl, isSatellite, sessionCache }),
    [environment, apiUrl, isSatellite, sessionCache],
  );
  return (
    <ClerkProvider
      publishableKey={pk}
      isSatellite={clerk?.isSatellite}
      domain={clerk?.domain}
      signInUrl={clerk?.signInUrl}
      signUpUrl={clerk?.signUpUrl}
      allowedRedirectOrigins={clerk?.allowedRedirectOrigins}
    >
      <SatelliteRedirectBack
        allowedRedirectOrigins={clerk?.allowedRedirectOrigins}
      />
      <DimeTerminalCtx.Provider value={ctx}>
        {children}
      </DimeTerminalCtx.Provider>
    </ClerkProvider>
  );
}
